<template>
  <div class="flex justify-center md:flex-row items-end m-6 h-0">
    <div class="flex md:text-center rounded-lg min-w-96 w-1/2 max-w-2xl welcome-test-card">
      <div class="flex px-3 py-3 items-center w-full">
        <div v-if="expanded" class="w-full text-gray-700">
          <div class="flex w-full justify-between">
            <div class="flex w-full">
              <ViceWelcomeTestResult :result="allPassed" :running="isRunning" />
              <h2
                class="font-semibold text-lg pb-4 pt-3"
                :class="{ 'text-vice-orange': !allPassed }"
              >
                VCE session {{ allPassed ? 'is possible' : 'limited and/or not possible' }}
              </h2>
            </div>
            <CrossGrayIcon class="cursor-pointer" @click="toggleDetails(false)" />
          </div>

          <div class="ml-16">
            <ul class="block text-sm leading-6">
              <li class="text-left mb-2">
                <span class="inline-flex mb-0 font-semibold">
                  <ViceWelcomeTestConnection :result="isInetCheckPassed" :running="isRunning" />
                  <span class="text-red-600" v-if="inetSpeedLevel === 1">
                    Internet connection insufficient
                  </span>
                  <span class="text-vice-orange" v-if="inetSpeedLevel === 2">
                    Internet connection limited
                  </span>
                  <span class="text-green-600" v-if="inetSpeedLevel === 3">
                    Internet connection OK
                  </span>
                  <span v-if="inetSpeedLevel === 4">Good internet connection</span>
                </span>
                <ul class="text-left text-xxs">
                  <li class="font-normal">
                    <span class="bold-highlights">
                      <CloudDown class="ml-8 inline-block" />
                      &nbsp;{{ downloadSpeed }}&nbsp;Mbps&nbsp;
                    </span>
                    down, enough for a session with {{ ableParticipantsNumber }} participants
                  </li>
                  <li class="font-normal">
                    <span class="bold-highlights">
                      <CloudUp class="ml-8 inline-block" />
                      &nbsp;{{ uploadSpeed }}&nbsp;Mbps&nbsp;
                    </span>
                    up, enough for a session with {{ ableDevicesNumber }} camera's and medical
                    devices
                  </li>
                </ul>
              </li>
              <li class="flex items-center mb-2 font-semibold">
                <ViceWelcomeTestConnection :result="serverConnection" :running="isRunning" />
                <span v-if="serverConnection">Connection to our servers OK</span>
                <span v-else>
                  None or partial connection to our servers
                  <button class="font-normal hover:underline" @click="toggleTestHostsAndPortsModal">
                    (see results)
                  </button>
                </span>
              </li>
              <li class="flex mb-2 font-semibold">
                <ViceWelcomeTestConnection :result="webRtcCheck" :running="isRunning" />
                {{ webRtcCheck ? '' : 'No' }} VCE test session possible
              </li>
              <li class="flex font-semibold sm:flex sm:items-center sm:justify-between">
                <span class="inline-flex">
                  <ViceWelcomeTestConnection :result="cameraCheck" :running="isRunning" />
                  {{ cameraCheck ? 'Camera' : 'No camera' }}&nbsp;present
                </span>
                <button
                  v-if="!allPassed"
                  @click="toggleIntercom"
                  type="button"
                  class="
                    inline-flex
                    items-center
                    px-4
                    border
                    text-xxs
                    font-medium
                    rounded-md
                    text-gray-500
                    bg-white
                  "
                >
                  Get help
                </button>
              </li>
            </ul>
          </div>
          <TestHostsAndPorts
            @toggleTestHostsAndPortsModal="toggleTestHostsAndPortsModal"
            @serverConnectionCheck="serverConnectionCheck"
            v-if="isTestHostsAndPorts"
            :hosts="hosts"
          />
        </div>

        <div v-else class="flex justify-between w-full">
          <div v-if="testPassed === true" class="inline-flex">
            <span class="test-succeed-icon mr-2.5" />
            <span class="font-normal text-vice-teal-700 text-sm content-center">
              VCE session is possible, connection is OK
            </span>
          </div>
          <div v-else-if="testPassed === false" class="inline-flex">
            <span class="test-failed-icon mr-2.5" />
            <span class="font-normal text-vice-red text-sm content-center">
              Session may not be possible
            </span>
          </div>
          <div v-else class="inline-flex">
            <RefreshIcon class="mr-2.5 spin" size="27" />
            <span class="font-normal text-gray-500 text-sm content-center">
              Testing connection...
            </span>
          </div>
          <div class="inline-flex">
            <span
              class="cursor-pointer text-xxs font-medium text-gray-500 content-center"
              @click="toggleDetails(true)"
            >
              More info
            </span>
            <button
              v-if="!allPassed"
              @click="toggleIntercom"
              type="button"
              class="
                inline-flex
                items-center
                px-4
                py-1
                ml-3
                border
                cursor-pointer
                text-xxs
                font-medium
                text-gray-500
                rounded-md
                bg-white
              "
            >
              Get help
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { showArticle } from '@intercom/messenger-js-sdk';
import CloudUp from '@/Components/Icons/CloudUp.vue';
import CloudDown from '@/Components/Icons/CloudDown.vue';
import TestHostsAndPorts from '@/Components/Modals/TestHostsAndPorts.vue';
import ViceWelcomeTestConnection from '@/Components/Icons/ViceWelcomeTestConnection.vue';
import ViceWelcomeTestResult from '@/Components/Icons/ViceWelcomeTestResult.vue';
import {
  APP_URL,
  CHAT_DOMAIN,
  TEST_IMAGE_SIZE,
  TURN_CREDENTIALS,
  TEST_IMAGE_CDN_URL,
  JITSI_SERVER_DOMAIN,
  IOT_WEBSOCKET_DOMAIN,
  LARAVEL_ECHO_SERVER_PORT,
  LARAVEL_ECHO_SERVER_HOST,
  LARAVEL_ECHO_SERVER_SCHEME,
  DEVICE_SCREEN_SHARE_APP_DOMAIN,
  DEVICE_WEBSOCKET_DOMAIN,
  DEVICE_WEBSOCKET_PORT,
} from '@/Utils/vice-constants';
import CrossGrayIcon from '@/Components/Icons/CrossGrayIcon.vue';
import RefreshIcon from '@/Components/Icons/RefreshIcon.vue';
import { capitalizeFirstLetter } from '@/Utils/helpers';

const DEFAULT_PORT = 443;
const DEFAULT_SCHEME = 'https';
const INTERCOM_ARTICLE = '9776046';

export default {
  name: 'WelcomeTestConnection',
  props: {
    testIsOpen: {
      type: Boolean,
      required: false,
    },
  },
  components: {
    RefreshIcon,
    CloudUp,
    CloudDown,
    CrossGrayIcon,
    TestHostsAndPorts,
    ViceWelcomeTestResult,
    ViceWelcomeTestConnection,
  },
  data() {
    return {
      testPassed: null,
      expanded: false,
      uploadSpeed: 0,
      downloadSpeed: 0,
      ableDevicesNumber: 0,
      ableParticipantsNumber: 0,
      webRtcCheck: true,
      cameraCheck: true,
      devicesCheck: true,
      participantsCheck: true,
      isInetCheckPassed: true,
      inetSpeedLevel: 4,
      serverConnection: true,
      isRunning: false,
      isTestHostsAndPorts: false,
      results: {},
      allPassed: true,
      hosts: [
        {
          scheme: LARAVEL_ECHO_SERVER_SCHEME,
          host: LARAVEL_ECHO_SERVER_HOST,
          port: process.env.NODE_ENV === 'production' ? DEFAULT_PORT : LARAVEL_ECHO_SERVER_PORT,
          purpose: 'Websocket server',
          status: '',
          class: null,
          passed: false,
        },
        {
          scheme: DEFAULT_SCHEME,
          host: JITSI_SERVER_DOMAIN,
          port: DEFAULT_PORT,
          purpose: 'Video conference server',
          status: '',
          class: null,
          passed: false,
        },
        {
          scheme: DEFAULT_SCHEME,
          host: CHAT_DOMAIN,
          port: DEFAULT_PORT,
          purpose: 'VCE chat service',
          status: '',
          class: null,
          passed: false,
        },
        {
          scheme: 'wss',
          host: DEVICE_WEBSOCKET_DOMAIN,
          port: DEVICE_WEBSOCKET_PORT, // todo: replace with 443 by Nginx proxy
          purpose: 'Android signaling server',
          status: '',
          class: null,
          passed: false,
        },
        {
          scheme: 'wss',
          host: IOT_WEBSOCKET_DOMAIN,
          port: DEFAULT_PORT,
          purpose: 'IOT Websocket Client',
          status: '',
          class: null,
          passed: false,
        },
      ],
      timeout: 30000,
    };
  },
  watch: {
    isOpen(opened) {
      if (opened) this.runChecks();
    },
  },
  mounted() {
    this.runChecks();
  },
  methods: {
    toggleTestHostsAndPortsModal(shouldShow = false) {
      this.isTestHostsAndPorts = shouldShow;
    },

    serverConnectionCheck(hasPassed) {
      this.serverConnection = hasPassed;
    },

    async runChecks() {
      this.isRunning = true;
      await Promise.all([
        this.withTimeout(this.checkDownloadSpeed(), this.timeout),
        this.withTimeout(this.checkUploadSpeed(), this.timeout),
        this.withTimeout(this.checkWebRTCConnection(), this.timeout),
        this.withTimeout(this.checkCameraPresent(), this.timeout),
        this.withTimeout(this.checkServerConnection(this.hosts), this.timeout),
      ]);

      this.inetSpeedLevel = this.getInetSpeedLevel();
      this.results.enoughInetSpeed = this.inetSpeedLevel > 2;
      this.isInetCheckPassed = this.downloadSpeed && this.uploadSpeed && this.inetSpeedLevel > 1;
      this.allPassed = !Object.values(this.results).includes(false);
      this.testPassed = this.allPassed;
      this.isRunning = false;
    },

    getInetSpeedLevel() {
      if (this.downloadSpeed < 10 || this.uploadSpeed < 5) return 1;
      if (this.downloadSpeed < 20 || this.uploadSpeed < 10) return 2;
      if (this.downloadSpeed > 40 && this.uploadSpeed > 20) return 4;
      if (this.downloadSpeed > 20 && this.uploadSpeed > 10) return 3;

      return 4;
    },

    withTimeout(promise, timeout) {
      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          console.error('Connection test timed out');
          resolve();
        }, timeout);

        promise
          .then((result) => {
            clearTimeout(timer);
            resolve(result);
          })
          .catch((error) => {
            clearTimeout(timer);
            reject(error);
          });
      });
    },

    async checkServerConnection() {
      const report = await TestHostsAndPorts.methods.shortCheckAllResources(this.hosts);
      const passedValues = report.map((check) => check.passed);
      const hasFailed = passedValues.some((value) => value === false);

      this.serverConnection = !hasFailed;
      this.results.serverConnection = !hasFailed;
      this.results.hostPortReport = report;
    },

    async checkCameraPresent() {
      const permissions = ['camera', 'microphone'];
      for (const permission of permissions) {
        // eslint-disable-next-line no-await-in-loop
        await navigator.permissions.query({ name: permission }).then((result) => {
          if (result.state === 'denied') {
            this.cameraCheck = false;
            console.error(
              `${capitalizeFirstLetter(
                permission,
              )} access denied. Please enable it in the browser settings.`,
            );
          }
        });
      }

      await navigator.mediaDevices
        .getUserMedia({ video: true, audio: true })
        .then((stream) => {
          stream.getTracks().forEach((track) => track.stop());
          this.cameraCheck = true;
        })
        .catch((error) => {
          this.cameraCheck = false;
          console.error('Camera is unavailable or missing.', error);
        });

      this.results.cameraPresent = this.cameraCheck;
    },

    async checkWebRTCConnection() {
      this.results.webrtcTurnRelay = false;
      this.webRtcCheck = false;

      return new Promise((resolve) => {
        this.results.webrtcTurnRelay = false;
        this.webRtcCheck = false;

        this.results.webrtcSupport = !!window.RTCPeerConnection;
        const peerConnection = new RTCPeerConnection({
          iceServers: [
            {
              urls: `turn:${DEVICE_SCREEN_SHARE_APP_DOMAIN}:3478?transport=udp`,
              username: TURN_CREDENTIALS,
              credential: TURN_CREDENTIALS,
            },
          ],
          iceTransportPolicy: 'relay',
        });

        this.results.p2pConnection = true;

        peerConnection.onicecandidate = (event) => {
          if (
            event.candidate &&
            event.candidate.type === 'relay' &&
            event.candidate.relayProtocol === 'udp'
          ) {
            this.webRtcCheck = true;
            this.results.webrtcTurnRelay = true;

            this.results.udpTurnRelayIce = {
              type: event.candidate.type,
              port: event.candidate.port,
              url: event.candidate.url,
              relayProtocol: event.candidate.relayProtocol,
            };
            peerConnection.close();
            resolve();
          }
        };

        peerConnection.createDataChannel('data');
        peerConnection
          .createOffer()
          .then((offer) => peerConnection.setLocalDescription(offer))
          .catch((error) => {
            this.results.webrtcTurnRelayError = error;
            console.error('Error creating offer: ', error);
          });
      });
    },

    async checkDownloadSpeed() {
      return new Promise((resolve) => {
        const downloadUrl = TEST_IMAGE_CDN_URL;
        const download = new Image();
        const downloadSize = TEST_IMAGE_SIZE;
        const maxParticipants = 20;
        let endTime;

        download.onload = () => {
          endTime = performance.now();

          this.downloadSpeed = getSpeedResult();
          this.ableParticipantsNumber = this.getBandwidthAbility(
            this.downloadSpeed,
            2,
            maxParticipants,
          );

          this.participantsCheck = this.getCheckResult(
            this.ableParticipantsNumber,
            maxParticipants,
          );

          this.results.downloadSpeed = this.downloadSpeed;
          resolve();
        };

        download.onerror = () => {
          this.ableParticipantsNumber = 0;
          this.results.downloadSpeed = 0;

          this.participantsCheck = false;
          resolve();
        };

        const startTime = performance.now();
        download.src = `${downloadUrl}?no-cache=${startTime}`;

        const getSpeedResult = () => {
          const duration = (endTime - startTime) / 1000;
          const bitsLoaded = downloadSize * 8;

          return (bitsLoaded / duration / 1000 / 1000).toFixed(2);
        };
      });
    },

    async checkUploadSpeed() {
      return new Promise((resolve) => {
        let endTime;
        const maxDevices = 5;
        const http = new XMLHttpRequest();
        const uploadUrl = `${APP_URL}/check-upload`;
        const data = `d=${'x'.repeat(10 * 1024 * 1024)}`;

        http.open('POST', uploadUrl, true);
        http.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');

        http.onreadystatechange = () => {
          if (http.readyState === 4 && http.status === 200) {
            endTime = performance.now();
            this.uploadSpeed = getResult();
            this.ableDevicesNumber = this.getBandwidthAbility(this.uploadSpeed, 3, maxDevices);
            this.devicesCheck = this.getCheckResult(this.ableDevicesNumber, maxDevices);
            this.results.uploadSpeed = this.uploadSpeed;

            resolve();
          }
        };

        const startTime = performance.now();
        http.send(data);

        const getResult = () => {
          const duration = (endTime - startTime) / 1000;
          const bitsLoaded = data.length * 8;

          return (bitsLoaded / duration / 1000000).toFixed(2);
        };
      });
    },

    getBandwidthAbility(speed, costOne, max) {
      const number = speed / costOne;
      if (number >= max) return max;

      return Math.floor(number);
    },

    getCheckResult(count, max) {
      if (count >= max) {
        return true;
      }
      if (count >= 1) {
        return true;
      }
      this.results.speedTestPassed = false;

      return false;
    },

    toggleDetails(state = false) {
      this.expanded = state;
    },

    toggleIntercom() {
      showArticle(INTERCOM_ARTICLE);
    },
  },
};
</script>
<style scoped>
.welcome-test-card {
  z-index: 1;
  border-radius: 10px;
  border: 1px solid #f1dd8f;
  background: #fef8e8;
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.05);
}

.test-failed-icon {
  width: 8px;
  height: 27px;
  border-radius: 10px;
  background: #ff4343;
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.05);
}

.test-succeed-icon {
  width: 8px;
  height: 27px;
  border-radius: 10px;
  background: #0d9488;
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.05);
}

.bold-highlights {
  font-weight: 700;
}

button[disabled] {
  opacity: 0.7;
  color: #666666;
  border: 1px solid #999999;
  background-color: #ebebeb;
}

.spin {
  animation: spin-animation 0.5s infinite reverse;
  display: inline-block;
}

@keyframes spin-animation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(359deg);
  }
}
</style>
